import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Badge,
  Divider,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LogoutIcon from "@mui/icons-material/Logout";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
// import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StarBorderPurple500Icon from '@mui/icons-material/StarBorderPurple500';
import PaymentIcon from "@mui/icons-material/Payment";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import { useThemeContext } from "../context/ThemeContext";
import { fetchAdminDetails } from "../api/authApi";
import { fetchAdminNotifications, markNotificationAsRead } from "../api/notificationsApi";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";

interface HeaderProps {
  onMenuClick: () => void;
  onLogout: () => void;
}

interface Notification {
  id: number;
  type: string;
  message: string;
  created_at: string;
  is_read: boolean;
  metadata?: {
    orderId?: number;
  };
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, onLogout }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [adminName, setAdminName] = useState<string>("");
  const [adminEmail, setAdminEmail] = useState<string>("");
  const { toggleTheme, isDarkMode } = useThemeContext();
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const admin = await fetchAdminDetails();
      if (admin) {
        setAdminName(admin.name);
        setAdminEmail(admin.email);
      }
    };
    fetchData();
  }, []);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const data = await fetchAdminNotifications();
        setNotifications(data);
      } catch (error) {
        console.error("Error loading notifications:", error);
      }
    };
  
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
  
    return () => clearInterval(interval);
  }, []);

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const markAsRead = async (notificationId: number) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const formatNotificationDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Just now";
      }
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (e) {
      return "Just now";
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_order':
        return <StarBorderPurple500Icon fontSize="small" color="primary" />;
      case 'payment_update':
        return <PaymentIcon fontSize="small" color="secondary" />;
      default:
        return <AnnouncementIcon fontSize="small" color="info" />;
    }
  };

  const NotificationDropdown = () => (
    <Menu
      anchorEl={notificationAnchor}
      open={Boolean(notificationAnchor)}
      onClose={handleNotificationClose}
      PaperProps={{
        sx: {
          width: 400,
          maxHeight: 500,
          overflow: 'auto',
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          '& .MuiMenuItem-root': {
            py: 1.5,
            alignItems: 'flex-start',
          },
        }
      }}
      MenuListProps={{
        sx: { py: 0 }
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          MOST RECENT
        </Typography>
      </Box>
      <Divider />

      {notifications.length === 0 ? (
        <MenuItem disabled sx={{ py: 2 }}>
          <ListItemText
            primary="No new notifications"
            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
          />
        </MenuItem>
      ) : (
        notifications.map((notification) => (
          <React.Fragment key={notification.id}>
            <MenuItem
              onClick={() => {
                markAsRead(notification.id);
                handleNotificationClose();
                const orderId = notification.metadata?.orderId;
                if (notification.type === "new_order" && orderId) {
                  navigate(`/dashboard/orders/${orderId}`);
                }
              }}
              sx={{
                '&:hover': { backgroundColor: 'action.hover' },
                borderLeft: 3,
                borderLeftColor: notification.type === 'payment_update' ? 'error.main' : 'divider',
              }}
            >
              <ListItemIcon sx={{ minWidth: '36px' }}>
                {getNotificationIcon(notification.type)}
              </ListItemIcon>
              <ListItemText
                primary={notification.message}
                secondary={
                  <>
                    {formatNotificationDate(notification.created_at)}
                    <Box component="span" sx={{ mx: 0.5 }}>·</Box>
                    {notification.type.replace('_', ' ')}
                  </>
                }
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: 500,
                  color: 'text.primary',
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                  component: 'div',
                }}
              />
            </MenuItem>
            <Divider />
          </React.Fragment>
        ))
      )}
    </Menu>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: 1300,
        bgcolor: "background.paper",
        color: "text.primary",
        boxShadow: "none",
        borderBottom: "1px solid",
        borderColor: "divider",
        backdropFilter: "blur(8px)",
        background: isDarkMode 
          ? "rgba(18, 18, 18, 0.8)"
          : "rgba(255, 255, 255, 0.8)",
      }}
    >
      <Toolbar sx={{ minHeight: "64px!important" }}>
        <IconButton
          edge="start"
          onClick={onMenuClick}
          sx={{
            mr: 2,
            display: { xs: "flex", md: "none" },
            color: "text.primary",
          }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: "-0.5px",
            mr: "auto",
            background: isDarkMode
              ? "linear-gradient(45deg, #90caf9, #1e88e5)"
              : "linear-gradient(45deg, #1e88e5, #0d47a1)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            flexGrow: 1,
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        >
          GG
        </Typography>

        <Tooltip title="Notifications">
          <IconButton color="inherit" onClick={handleNotificationOpen}>
            <Badge badgeContent={notifications.length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>
        
        <NotificationDropdown />

        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          mx: 1,
          bgcolor: "action.hover",
          borderRadius: 2,
          px: 1,
          py: 0.5
        }}>
          {isDarkMode ? (
            <DarkModeIcon fontSize="small" sx={{ mr: 0.5 }} />
          ) : (
            <LightModeIcon fontSize="small" sx={{ mr: 0.5 }} />
          )}
          <Switch
            checked={isDarkMode}
            onChange={toggleTheme}
            size="small"
            sx={{
              "& .MuiSwitch-track": { borderRadius: 20 },
              "& .MuiSwitch-thumb": { boxShadow: "0 2px 4px rgba(0,0,0,0.2)" }
            }}
          />
        </Box>

        <Tooltip title="Account settings">
          <IconButton onClick={handleAvatarClick} sx={{ ml: 1 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "primary.main",
                fontWeight: 500,
              }}
            >
              {adminName?.charAt(0) || "A"}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: {
                mt: 1.5,
                minWidth: 220,
                boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                borderRadius: 2,
                "& .MuiMenuItem-root": {
                  px: 2,
                  py: 1.5,
                  typography: "body2",
                },
              },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2">{adminName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {adminEmail}
            </Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <MenuItem onClick={onLogout}>
            <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} />
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;